/**
 * Saved mash and fermentation schedule commands (PLAN-PROFILES P1).
 *
 * Two libraries, four verbs each, spelled out — the same longhand `workspace.ts`
 * uses for equipment and water, and for the same reason.
 *
 * **A factory was tried first and backed out.** The two libraries differ in
 * nothing but the record they live in, so one parameterised builder looked like
 * the honest way to say that. It was not: `defineCommand` keys off literal
 * command types, `priorValues` and `mergeDefined` cannot narrow a union of two
 * document shapes, and the version that compiled needed a cast on every payload,
 * every command type and every inverse. Casts are precisely where a divergence
 * between the two libraries would hide, which is what the factory existed to
 * prevent. Longhand costs repetition and buys the compiler back.
 *
 * What *is* shared is the logic that has no types to fight over: putting a
 * profile back, taking one out, and the refusal that guards the built-ins.
 *
 * ## Applying is not here
 *
 * There is no `recipe.applyMashProfile`. Applying copies steps into a recipe
 * through the existing `recipe.addMashStep` / `removeMashStep` pair in one
 * `dispatchAll`, exactly as the decoction preset menu already does. A command
 * would be a second way to do a thing the log can already express, and it would
 * have to duplicate the step-id minting. See PLAN-PROFILES P2.
 */

import type { FermentationProfileDoc, MashProfileDoc } from '../../document/schedule-profile';
import type { WorkspaceState } from '../../document/workspace';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import { mergeDefined, priorValues } from '../helpers';
import * as p from '../payloads';

/** Put a mash profile back into the workspace. */
function putMash(state: WorkspaceState, profile: MashProfileDoc): WorkspaceState {
  return { ...state, mashProfiles: { ...state.mashProfiles, [profile.id]: profile } };
}

/** Put a fermentation profile back into the workspace. */
function putFerm(state: WorkspaceState, profile: FermentationProfileDoc): WorkspaceState {
  return {
    ...state,
    fermentationProfiles: { ...state.fermentationProfiles, [profile.id]: profile },
  };
}

/**
 * The refusal both `update` and `delete` share on either library.
 *
 * Built-ins are product data: a later build must be free to reword "Full body"
 * or move its rest, and it cannot be if a brewer has edited the copy in their
 * own workspace. Duplicating is the escape hatch, and the message says so.
 */
function refuseBuiltin(name: string, commandId: string, commandType: string) {
  return commandError(
    'invalid-state',
    `${quoted(name)} is a built-in schedule. Duplicate it to make your own version.`,
    {
      commandId,
      commandType,
    },
  );
}

/** The "no such profile" refusal, worded per library. */
function notFound(noun: string, profileId: string, commandId: string, commandType: string) {
  return commandError('not-found', `No ${noun} with id "${profileId}".`, {
    commandId,
    commandType,
  });
}

// ---------------------------------------------------------------------------
// Mash schedules
// ---------------------------------------------------------------------------

/** Save a mash schedule, blank or duplicated from another. */
export const mashProfileCreate = defineCommand({
  type: 'mashProfile.create',
  payload: p.mashProfileCreatePayloadSchema,
  apply: (state, command) => {
    const { profileId, name, from } = command.payload;
    if (state.mashProfiles[profileId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `A mash schedule with id "${profileId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const profile: MashProfileDoc = {
      notes: from?.notes ?? '',
      steps: from?.steps ?? [],
      id: profileId,
      name,
      builtin: false,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putMash(state, profile) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'mashProfile.delete',
      { profileId: command.payload.profileId },
      command.timestamp,
    ),
  summarize: (_state, command) => `Save mash schedule ${quoted(command.payload.name)}`,
});

/** Change a saved mash schedule. Built-ins refuse. */
export const mashProfileUpdate = defineCommand({
  type: 'mashProfile.update',
  payload: p.mashProfileUpdatePayloadSchema,
  apply: (state, command) => {
    const profile = state.mashProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: notFound(
          'mash schedule',
          command.payload.profileId,
          command.commandId,
          command.type,
        ),
      };
    }
    if (profile.builtin) {
      return { ok: false, error: refuseBuiltin(profile.name, command.commandId, command.type) };
    }
    return {
      ok: true,
      state: putMash(state, {
        ...mergeDefined(profile, command.payload.changes),
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const profile = state.mashProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(
          command,
          'mashProfile.update',
          { profileId: profile.id, changes: priorValues(profile, command.payload.changes) },
          profile.updatedAt,
        );
  },
  summarize: (state, command) => {
    const profile = state.mashProfiles[command.payload.profileId];
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update mash schedule ${quoted(profile?.name ?? command.payload.profileId)}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Delete a saved mash schedule. Built-ins refuse. */
export const mashProfileDelete = defineCommand({
  type: 'mashProfile.delete',
  payload: p.mashProfileDeletePayloadSchema,
  apply: (state, command) => {
    const profile = state.mashProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: notFound(
          'mash schedule',
          command.payload.profileId,
          command.commandId,
          command.type,
        ),
      };
    }
    if (profile.builtin) {
      return { ok: false, error: refuseBuiltin(profile.name, command.commandId, command.type) };
    }
    const next = { ...state.mashProfiles };
    delete next[command.payload.profileId];
    return { ok: true, state: { ...state, mashProfiles: next } };
  },
  // Inverts to a restore rather than a create, so undo is an identity: the
  // timestamps come back too. Same reason `equipment.delete` does.
  invert: (state, command) => {
    const profile = state.mashProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(command, 'mashProfile.restore', { profile }, profile.updatedAt);
  },
  summarize: (state, command) => {
    const profile = state.mashProfiles[command.payload.profileId];
    return `Delete mash schedule ${quoted(profile?.name ?? command.payload.profileId)}`;
  },
});

/** Insert a saved mash schedule verbatim — the inverse of a delete. */
export const mashProfileRestore = defineCommand({
  type: 'mashProfile.restore',
  payload: p.mashProfileRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putMash(state, command.payload.profile) }),
  invert: (_state, command) =>
    inverseCommand(
      command,
      'mashProfile.delete',
      { profileId: command.payload.profile.id },
      command.timestamp,
    ),
  summarize: (_state, command) => `Restore mash schedule ${quoted(command.payload.profile.name)}`,
});

// ---------------------------------------------------------------------------
// Fermentation schedules
// ---------------------------------------------------------------------------

/** Save a fermentation schedule, blank or duplicated from another. */
export const fermentationProfileCreate = defineCommand({
  type: 'fermentationProfile.create',
  payload: p.fermentationProfileCreatePayloadSchema,
  apply: (state, command) => {
    const { profileId, name, from } = command.payload;
    if (state.fermentationProfiles[profileId] !== undefined) {
      return {
        ok: false,
        error: commandError(
          'conflict',
          `A fermentation schedule with id "${profileId}" already exists.`,
          {
            commandId: command.commandId,
            commandType: command.type,
          },
        ),
      };
    }
    const profile: FermentationProfileDoc = {
      notes: from?.notes ?? '',
      steps: from?.steps ?? [],
      id: profileId,
      name,
      builtin: false,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putFerm(state, profile) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'fermentationProfile.delete',
      { profileId: command.payload.profileId },
      command.timestamp,
    ),
  summarize: (_state, command) => `Save fermentation schedule ${quoted(command.payload.name)}`,
});

/** Change a saved fermentation schedule. Built-ins refuse. */
export const fermentationProfileUpdate = defineCommand({
  type: 'fermentationProfile.update',
  payload: p.fermentationProfileUpdatePayloadSchema,
  apply: (state, command) => {
    const profile = state.fermentationProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: notFound(
          'fermentation schedule',
          command.payload.profileId,
          command.commandId,
          command.type,
        ),
      };
    }
    if (profile.builtin) {
      return { ok: false, error: refuseBuiltin(profile.name, command.commandId, command.type) };
    }
    return {
      ok: true,
      state: putFerm(state, {
        ...mergeDefined(profile, command.payload.changes),
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const profile = state.fermentationProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(
          command,
          'fermentationProfile.update',
          { profileId: profile.id, changes: priorValues(profile, command.payload.changes) },
          profile.updatedAt,
        );
  },
  summarize: (state, command) => {
    const profile = state.fermentationProfiles[command.payload.profileId];
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update fermentation schedule ${quoted(profile?.name ?? command.payload.profileId)}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Delete a saved fermentation schedule. Built-ins refuse. */
export const fermentationProfileDelete = defineCommand({
  type: 'fermentationProfile.delete',
  payload: p.fermentationProfileDeletePayloadSchema,
  apply: (state, command) => {
    const profile = state.fermentationProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: notFound(
          'fermentation schedule',
          command.payload.profileId,
          command.commandId,
          command.type,
        ),
      };
    }
    if (profile.builtin) {
      return { ok: false, error: refuseBuiltin(profile.name, command.commandId, command.type) };
    }
    const next = { ...state.fermentationProfiles };
    delete next[command.payload.profileId];
    return { ok: true, state: { ...state, fermentationProfiles: next } };
  },
  // Inverts to a restore rather than a create, so undo is an identity: the
  // timestamps come back too. Same reason `equipment.delete` does.
  invert: (state, command) => {
    const profile = state.fermentationProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(command, 'fermentationProfile.restore', { profile }, profile.updatedAt);
  },
  summarize: (state, command) => {
    const profile = state.fermentationProfiles[command.payload.profileId];
    return `Delete fermentation schedule ${quoted(profile?.name ?? command.payload.profileId)}`;
  },
});

/** Insert a saved fermentation schedule verbatim — the inverse of a delete. */
export const fermentationProfileRestore = defineCommand({
  type: 'fermentationProfile.restore',
  payload: p.fermentationProfileRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putFerm(state, command.payload.profile) }),
  invert: (_state, command) =>
    inverseCommand(
      command,
      'fermentationProfile.delete',
      { profileId: command.payload.profile.id },
      command.timestamp,
    ),
  summarize: (_state, command) =>
    `Restore fermentation schedule ${quoted(command.payload.profile.name)}`,
});
